﻿import { useEffect } from "react";
import type { BaselineData, ExecutionData } from "../types";

export function useProjectKpi(
  baselineData: BaselineData,
  executionData: ExecutionData,
  managerNote: string,
) {
  useEffect(() => {
    const approvedTasks = baselineData.tasks.filter((t) =>
      (executionData.validatedTasks || []).includes(t.id),
    );
    const totalTasks = baselineData.tasks.length;
    const approvedCount = approvedTasks.length;

    const physicalProgress =
      baselineData.status === "completed"
        ? 100
        : totalTasks > 0
        ? Math.round(
            (() => {
              const tasks = baselineData.tasks.map((t) => ({
                ...t,
                status: (executionData.validatedTasks || []).includes(t.id)
                  ? "completed"
                  : "in-progress",
                progress: executionData.taskProgress?.[t.id as string] || 0,
              }));
              const achievedWeight = tasks.reduce((sum, task) => {
                if (task.status === "completed") return sum + Number(task.weight || 0);
                return sum + (Number(task.weight || 0) * Number(task.progress || 0)) / 100;
              }, 0);
              const totalWeight = tasks.reduce((sum, task) => sum + Number(task.weight || 0), 0);
              return totalWeight > 0 ? (achievedWeight / totalWeight) * 100 : 0;
            })(),
          )
        : 0;

    const today = new Date();
    const deadlineDate = baselineData.deadline ? new Date(baselineData.deadline) : null;
    const daysRemaining = deadlineDate
      ? Math.max(
          0,
          Math.ceil((deadlineDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)),
        )
      : null;

    const totalExpense =
      executionData.actualExpenses.reduce((sum, e) => sum + e.amount, 0) +
      baselineData.vendors
        .filter((v) => executionData.usedVendorIds.includes(v.id ?? "__missing__"))
        .reduce((sum, v) => sum + (v.cost || 0), 0);

    const healthScore = physicalProgress >= 75 ? 90 : physicalProgress >= 50 ? 65 : 30;

    const milestoneTimeline = approvedTasks.map((t) => ({
      name: t.name,
      date: today.toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }),
      milestone:
        baselineData.milestones.find((m) => m.id === t.milestone_id)?.label || "Milestone",
      type: "done",
    }));

    const recentActivities = [
      ...milestoneTimeline.map((m) => ({
        task_name: `${m.name} — Selesai & Approved`,
        team: "Tab 3 Validasi",
        time: "Baru saja",
        type: "done",
      })),
      ...Object.entries(executionData.taskProgress || {})
        .filter(([, p]) => p > 0 && p < 100)
        .map(([id, p]) => {
          const task = baselineData.tasks.find((t) => t.id === Number(id));
          return {
            task_name: `${task?.name || "Tugas"} — Progress ${p}%`,
            team: "Tab 2 Eksekusi",
            time: "Baru saja",
            type: "update",
          };
        }),
      ...executionData.actualExpenses.slice(-2).map((e) => ({
        task_name: `Pengeluaran: ${e.description || "Material"}`,
        team: "Logistik",
        time: "Baru saja",
        type: "material",
      })),
    ].slice(0, 6);

    localStorage.setItem(
      "MANPRO_kpis",
      JSON.stringify({
        physical_progress: physicalProgress,
        approved_count: approvedCount,
        total_tasks: totalTasks,
        days_remaining: daysRemaining,
        deadline: baselineData.deadline,
        total_expense: totalExpense,
        budget: baselineData.budget,
        health_score: healthScore,
        active_projects: approvedCount > 0 ? 1 : totalTasks > 0 ? 1 : 0,
        milestone_timeline: milestoneTimeline,
        recent_activities: recentActivities,
        manager_note: managerNote,
        last_updated: today.toISOString(),
      }),
    );
  }, [baselineData, executionData, managerNote]);
}
